import { getEuropeCountryByCode } from "@shared/europe-countries";
import type { AppLocale } from "@shared/i18n-locales";

export interface RegionInfo {
    regionName: string;
    isCity?: boolean;
    isProvince?: boolean;
    isCountry?: boolean;
    countryCode?: string;
    stateOrProvince?: string;
}

/**
 * Generates a localized SEO-friendly region string based on country and state/province.
 * 
 * Rules:
 * - NL: "Nederland en de rest van Europa"
 * - EU: "{Country} en Europa"
 * - US: "{State} en de Verenigde Staten"
 * - CA: "Canada en de Verenigde Staten"
 */
export function getLocalizedRegionSEO(info: RegionInfo, locale: AppLocale = "nl"): string {
    const { regionName, countryCode, stateOrProvince, isCity } = info;

    // If it's a city, just return the name for the template to handle
    if (isCity) return regionName;

    const normalizedCountry = countryCode?.toUpperCase();

    if (normalizedCountry === "NL") {
        return locale === "nl" ? "Nederland en de rest van Europa" : "the Netherlands and the rest of Europe";
    }

    if (normalizedCountry === "US") {
        const state = stateOrProvince || regionName;
        return locale === "nl" ? `${state} en de Verenigde Staten` : `${state} and the United States`;
    }

    if (normalizedCountry === "CA") {
        return locale === "nl" ? "Canada en de Verenigde Staten" : "Canada and the United States";
    }

    // Check if it's a European country
    const euCountry = getEuropeCountryByCode(normalizedCountry);
    if (euCountry) {
        const countryLabel = locale === "nl" ? euCountry.labelNl : euCountry.labelEn;
        return `${countryLabel} ${locale === "nl" ? "en Europa" : "and Europe"}`;
    }

    return regionName;
}
